import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api';
import { useMe } from '../auth/useMe';
import { todayInWarsaw } from '../events/calendar';
import { DialogLayer } from '../ui/Dialog';
import { trapTab } from '../ui/focus';
import { RowMenu } from '../ui/RowMenu';
import { dayHeader, groupEntries, PERIODS, periodCounts, storedPeriod, storePeriod, type Period } from './period';
import { archiveBatch, dayText, MAX_ARCHIVE, notificationId, plusDays, snoozeChoices, SUMMARY_KEY, type SummaryEntry, type SummaryView } from './summary';
import { SEVERITY_LABELS, TODO_KEY } from './types';
import './todo.css';

/** „Inna data…”: a day after today, at most a year ahead (as the server checks). */
function OtherDayDialog({ entry, today, onPick, onClose, busy }: { entry: SummaryEntry; today: string; onPick: (day: string) => void; onClose: () => void; busy: boolean }) {
  const [day, setDay] = useState(plusDays(today, 2));
  const valid = day > today && day <= plusDays(today, 366);
  return (
    <div className="dialog-backdrop">
      <div className="dialog panel" role="dialog" aria-modal="true" aria-labelledby="snooze-title" onKeyDown={(e) => e.key === 'Tab' && trapTab(e.currentTarget, e.nativeEvent)}>
        <DialogLayer onClose={onClose} busy={busy} />
        <h2 id="snooze-title">Przesuń na inny dzień</h2>
        <p className="muted">{entry.title}</p>
        <label className="field">
          <span>Wróci na listę</span>
          <input type="date" value={day} min={plusDays(today, 1)} max={plusDays(today, 366)} onChange={(e) => setDay(e.target.value)} autoFocus />
        </label>
        <div className="actions">
          <button className="btn primary" type="button" disabled={!valid || busy} onClick={() => onPick(day)}>
            Przesuń
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}

function Entry({
  entry,
  today,
  overdue = false,
  selected,
  onSelect,
  onSnooze,
  onOther,
  onRestore,
  onArchive,
  onOpen,
  onPickStart,
  onPickOver,
}: {
  entry: SummaryEntry;
  today: string;
  overdue?: boolean;
  selected: boolean;
  onSelect: (on: boolean) => void;
  onSnooze: (day: string) => void;
  onOther: () => void;
  onRestore: () => void;
  onArchive: () => void;
  onOpen: () => void;
  onPickStart: (e: ReactPointerEvent) => void;
  onPickOver: (e: ReactPointerEvent) => void;
}) {
  const actions = [
    ...(entry.snoozedUntil ? [{ label: 'Przywróć teraz', onSelect: onRestore }] : [...snoozeChoices(today).map((c) => ({ label: c.label, onSelect: () => onSnooze(c.day) })), { label: 'Inna data…', onSelect: onOther }]),
    { label: 'Archiwizuj', onSelect: onArchive },
  ];
  return (
    <li className={`todo-row ${entry.severity}${overdue ? ' overdue' : ''}${entry.snoozedUntil ? ' snoozed' : ''}`} onPointerOver={onPickOver}>
      <input type="checkbox" className="summary-pick" aria-label={`Zaznacz: ${entry.title}`} checked={selected} onChange={(e) => onSelect(e.target.checked)} onPointerDown={onPickStart} />
      <span className="todo-mark" role="img" aria-label={SEVERITY_LABELS[entry.severity]} />
      <span className="todo-main">
        <Link to={entry.link} className="todo-title" onClick={onOpen}>
          {entry.title}
        </Link>
        {(entry.detail !== '' || entry.snoozedUntil || overdue) && (
          <span className="todo-detail">{[overdue && entry.dueDay ? dayHeader(entry.dueDay, today) : '', entry.detail, entry.snoozedUntil ? `przesunięte do ${dayText(entry.snoozedUntil)}` : ''].filter((x) => x !== '').join(' · ')}</span>
        )}
      </span>
      <span className="todo-actions">
        <RowMenu label={entry.snoozedUntil ? `Przywróć: ${entry.title}` : `Przesuń: ${entry.title}`} text={entry.snoozedUntil ? 'Przywróć' : 'Przesuń'} actions={actions} floating="end" />
      </span>
    </li>
  );
}

/** An archived entry (at the bottom, under „Pokaż zarchiwizowane”): its link and „Przywróć”. */
function ArchivedEntry({ entry, onRestore, onOpen }: { entry: SummaryEntry; onRestore: () => void; onOpen: () => void }) {
  return (
    <li className={`todo-row ${entry.severity} archived`}>
      <span className="todo-mark" role="img" aria-label={SEVERITY_LABELS[entry.severity]} />
      <span className="todo-main">
        <Link to={entry.link} className="todo-title" onClick={onOpen}>
          {entry.title}
        </Link>
        {entry.detail !== '' && <span className="todo-detail">{entry.detail}</span>}
      </span>
      <span className="todo-actions">
        <button type="button" className="link-button" aria-label={`Przywróć: ${entry.title}`} onClick={onRestore}>
          Przywróć
        </button>
      </span>
    </li>
  );
}

/**
 * "Podsumowanie" (owner 2026-10-24 01:46: „podsumowanie dzienne chce zakladke na wierzchu … z mozliwoscia przesuniecia
 * na jutro za tydzien inna date”): the sections the modules give (Brak wyceny, Faktury do wystawienia, Zadania
 * niewykonane, Powiadomienia), each entry a link to where it is done, and „Przesuń” per entry (only for me).
 * Owner 2026-10-30 22:49 (option 1): „Ten tydzień · Ten miesiąc · Wszystkie” above the sections (the last choice kept
 * per user in this browser). Inside each section: „Zaległe” in red on top, then a header per day from the nearest,
 * „Bez daty” at the end (`groupEntries`). The server sends every entry with its date, so the filter is here.
 * „Archiwizuj” (owner 2026-11-03 23:37): a box at each entry and „Archiwizuj zaznaczone” on top (only the ticked
 * entries shown now, in the chosen period), „Archiwizuj” in the entry's menu; the archived ones (the server keeps them
 * out of every list and count here) under the text link „Pokaż zarchiwizowane (N)” at the bottom, „Przywróć” at each.
 * Ticking by dragging (owner 2026-11-04 09:47): see `pickStart`.
 */
export function SummaryPage() {
  const queryClient = useQueryClient();
  const summary = useQuery({ queryKey: SUMMARY_KEY, queryFn: () => api<SummaryView>('/api/todo/summary') });
  const [showMoved, setShowMoved] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const [other, setOther] = useState<SummaryEntry | null>(null);
  const { me } = useMe();
  const [chosen, setChosen] = useState<Period | null>(null);
  const period = chosen ?? storedPeriod(me?.id);
  const choose = (p: Period) => {
    setChosen(p);
    storePeriod(me?.id, p);
  };
  const today = todayInWarsaw();
  const refresh = () => Promise.all([queryClient.invalidateQueries({ queryKey: SUMMARY_KEY }), queryClient.invalidateQueries({ queryKey: TODO_KEY })]);
  const snooze = useMutation({
    mutationFn: (v: { itemId: string; untilDay: string }) => api('/api/todo/snoozes', { method: 'PUT', body: v }),
    onSuccess: () => setOther(null),
    onSettled: refresh,
  });
  const restore = useMutation({ mutationFn: (itemId: string) => api(`/api/todo/snoozes?itemId=${encodeURIComponent(itemId)}`, { method: 'DELETE' }), onSettled: refresh });
  const archive = useMutation({
    mutationFn: (itemIds: string[]) => api('/api/todo/archive', { method: 'PUT', body: { itemIds } }),
    onSuccess: (_r, itemIds) => setPicked((p) => new Set([...p].filter((id) => !itemIds.includes(id)))),
    onSettled: refresh,
  });
  const unarchive = useMutation({ mutationFn: (itemId: string) => api(`/api/todo/archive?itemId=${encodeURIComponent(itemId)}`, { method: 'DELETE' }), onSettled: refresh });
  // opening a bell entry reads it (as in the bell)
  const read = (entry: SummaryEntry) => {
    const id = notificationId(entry);
    if (id) void api(`/api/me/notifications/${id}/read`, { method: 'POST' }).then(refresh, () => undefined);
  };
  const error = [summary.error, snooze.error, restore.error, archive.error, unarchive.error].find((e) => e);
  const data = summary.data;
  const counts = data ? periodCounts(data.sections.flatMap((s) => s.items), today) : null;
  // the entries on the screen now (the period, „Pokaż przesunięte”): only those are archived by „Archiwizuj zaznaczone”
  const shownOrder = (data?.sections ?? []).flatMap((s) => [...groupEntries(s.items, period, today).flatMap((g) => g.entries), ...(showMoved ? s.snoozed : [])]).map((e) => e.id);
  const shownIds = new Set(shownOrder);
  const { ids: toArchive, tooMany } = archiveBatch(picked, shownIds);
  // a tick on an entry no longer on the screen (another period, „Ukryj przesunięte”, gone after a refresh) is dropped
  const shownKey = [...shownIds].join('\n');
  useEffect(() => {
    if (!data) return;
    const shown = new Set(shownKey.split('\n'));
    setPicked((p) => ([...p].every((id) => shown.has(id)) ? p : new Set([...p].filter((id) => shown.has(id)))));
  }, [shownKey, data]);
  // Ticking by dragging (owner 2026-11-04 09:47: „zaznaczac wiecej pozycji klikajac na pierwsze i przeciagajac myszka
  // w dol”): a mouse/pen press on a box flips it at once; every entry the pointer then passes (down or up, also into the
  // next sections) gets the same state; a fast move fills the entries skipped between, in the order on the screen. Rows
  // passed and left again stay as set. The click the browser sends after the press is swallowed (the press already
  // flipped the box), so a plain click toggles once. Touch keeps the plain tap. Releasing anywhere ends the gesture.
  const pickDrag = useRef<{ value: boolean; lastId: string } | null>(null);
  const swallowChange = useRef(false);
  const [picking, setPicking] = useState(false);
  const setMany = (ids: readonly string[], on: boolean) =>
    setPicked((p) => {
      const next = new Set(p);
      for (const id of ids) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  const pickEnd = () => {
    if (!pickDrag.current) return;
    pickDrag.current = null;
    setPicking(false);
    setTimeout(() => {
      swallowChange.current = false;
    }, 0);
  };
  const pickStart = (id: string, e: ReactPointerEvent) => {
    // mouse only (touch and pen keep the plain tap); a modifier press (Ctrl+click = Mac context menu) is a plain click — review M1/M3
    if (e.pointerType !== 'mouse' || e.button !== 0 || e.ctrlKey || e.metaKey || e.altKey || e.shiftKey || !shownIds.has(id)) return;
    e.preventDefault(); // no text selection, no dragging of a link
    const value = !picked.has(id);
    pickDrag.current = { value, lastId: id };
    swallowChange.current = true;
    setMany([id], value);
    setPicking(true);
  };
  const pickOver = (id: string, e: ReactPointerEvent) => {
    const drag = pickDrag.current;
    if (!drag) return;
    if ((e.buttons & 1) === 0) return pickEnd(); // released outside the window
    const from = shownOrder.indexOf(drag.lastId);
    const to = shownOrder.indexOf(id);
    if (to < 0 || to === from) return;
    setMany(from < 0 ? [id] : shownOrder.slice(Math.min(from, to), Math.max(from, to) + 1), drag.value);
    drag.lastId = id;
  };
  useEffect(() => {
    if (!picking) return;
    const end = () => pickEnd();
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
    window.addEventListener('blur', end);
    window.addEventListener('contextmenu', end);
    return () => {
      window.removeEventListener('contextmenu', end);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
      window.removeEventListener('blur', end);
    };
  }, [picking]);
  const archivedSections = (data?.sections ?? []).filter((s) => (s.archived ?? []).length > 0);
  const archivedCount = data?.archivedCount ?? 0;
  const entryProps = (e: SummaryEntry) => ({
    entry: e,
    today,
    selected: picked.has(e.id),
    onSelect: (on: boolean) => {
      if (!swallowChange.current) setMany([e.id], on);
    },
    onPickStart: (ev: ReactPointerEvent) => pickStart(e.id, ev),
    onPickOver: (ev: ReactPointerEvent) => pickOver(e.id, ev),
    onOpen: () => read(e),
    onSnooze: (day: string) => snooze.mutate({ itemId: e.id, untilDay: day }),
    onOther: () => setOther(e),
    onRestore: () => restore.mutate(e.id),
    onArchive: () => archive.mutate([e.id]),
  });
  return (
    <section className={`page narrow summary-page${picking ? ' picking' : ''}`} aria-labelledby="summary-title" data-feature-id="todo.summary">
      <h1 id="summary-title">Podsumowanie{data ? ` (${data.count})` : ''}</h1>
      <p className="muted">Co czeka: wydarzenia bez wyceny ze wszystkich dni, faktury, zadania, powiadomienia. „Przesuń” chowa wpis tylko u Ciebie — wróci w wybranym dniu. „Archiwizuj” chowa go tylko u Ciebie, aż go przywrócisz.</p>
      {summary.isLoading && (
        <p className="muted" role="status">
          Wczytywanie…
        </p>
      )}
      {error && (
        <p className="alert" role="alert">
          {error instanceof ApiError ? error.message : 'Nie udało się wczytać podsumowania'}
        </p>
      )}
      {counts && (
        <fieldset className="segmented summary-period">
          <legend>Okres</legend>
          {PERIODS.map((p) => (
            <label key={p.value} className={p.value === period ? 'on' : undefined}>
              <input type="radio" name="summary-period" value={p.value} checked={p.value === period} onChange={() => choose(p.value)} />
              {p.label} ({counts[p.value]})
            </label>
          ))}
        </fieldset>
      )}
      {data && data.count + data.snoozedCount > 0 && (
        <p className="summary-bulk">
          <button type="button" className="btn small" disabled={toArchive.length === 0 || tooMany || archive.isPending} onClick={() => archive.mutate(toArchive)}>
            Archiwizuj zaznaczone
          </button>
          {tooMany && <span className="muted"> Najwyżej {MAX_ARCHIVE} wpisów naraz.</span>}
        </p>
      )}
      {data?.sections.map((s) => {
        const moved = showMoved ? s.snoozed : [];
        const groups = groupEntries(s.items, period, today);
        const shown = groups.reduce((n, g) => n + g.entries.length, 0);
        return (
          <section key={s.id} className="summary-section" aria-labelledby={`summary-${s.id}`} data-section={s.id}>
            <h2 id={`summary-${s.id}`}>
              {s.title} <span className="muted">({shown})</span>
            </h2>
            {shown === 0 && moved.length === 0 && <p className="muted">{s.items.length === 0 ? 'Nic nie czeka.' : 'Nic w tym okresie.'}</p>}
            {groups.map((g) => (
              <div key={g.key} className={`summary-group ${g.tone}`}>
                <h3 className={`summary-day${g.tone === 'overdue' ? ' overdue' : ''}`}>{g.label}</h3>
                <ul className="todo-list" aria-label={`${s.title}: ${g.label}`}>
                  {g.entries.map((e) => (
                    <Entry key={e.id} {...entryProps(e)} overdue={g.tone === 'overdue'} />
                  ))}
                </ul>
              </div>
            ))}
            {moved.length > 0 && (
              <div className="summary-group moved">
                <h3 className="summary-day">Przesunięte</h3>
                <ul className="todo-list" aria-label={`${s.title}: przesunięte`}>
                  {moved.map((e) => (
                    <Entry key={e.id} {...entryProps(e)} />
                  ))}
                </ul>
              </div>
            )}
          </section>
        );
      })}
      {data && data.snoozedCount > 0 && (
        <p>
          <button type="button" className="btn ghost small" aria-pressed={showMoved} onClick={() => setShowMoved((v) => !v)}>
            {showMoved ? 'Ukryj przesunięte' : `Pokaż przesunięte (${data.snoozedCount})`}
          </button>
        </p>
      )}
      {archivedCount > 0 && (
        <p>
          <button type="button" className="link-button" aria-expanded={showArchived} onClick={() => setShowArchived((v) => !v)}>
            {showArchived ? 'Ukryj zarchiwizowane' : `Pokaż zarchiwizowane (${archivedCount})`}
          </button>
        </p>
      )}
      {showArchived && archivedCount > 0 && (
        <section className="summary-section summary-archived" aria-labelledby="summary-archived">
          <h2 id="summary-archived">Zarchiwizowane</h2>
          {archivedSections.map((s) => (
            <div key={s.id} className="summary-group">
              <h3 className="summary-day">{s.title}</h3>
              <ul className="todo-list" aria-label={`${s.title}: zarchiwizowane`}>
                {(s.archived ?? []).map((e) => (
                  <ArchivedEntry key={e.id} entry={e} onOpen={() => read(e)} onRestore={() => unarchive.mutate(e.id)} />
                ))}
              </ul>
            </div>
          ))}
        </section>
      )}
      <p className="muted summary-settings">
        Wiadomość z podsumowaniem codziennie o wybranej godzinie: <Link to="/moje-konto#podsumowanie-dzienne">Moje konto → Podsumowanie dzienne</Link>.
      </p>
      {other && <OtherDayDialog entry={other} today={today} busy={snooze.isPending} onClose={() => setOther(null)} onPick={(day) => snooze.mutate({ itemId: other.id, untilDay: day })} />}
    </section>
  );
}
